import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { organisationsAPI, streamsAPI, projectsAPI, accessRequestsAPI } from '../../services/api';
import { orgKeys, invalidateOrgMembersCaches } from '../../query/queryKeys';
import toast from '../../services/toast';

function buildMemberParams(debouncedSearch, memberFilter) {
  const searchParam =
    debouncedSearch != null && String(debouncedSearch).trim() !== ''
      ? debouncedSearch.trim()
      : undefined;
  const memberParams: any = { stats: true, search: searchParam };
  if (memberFilter.repositoryId) memberParams.repositoryId = memberFilter.repositoryId;
  else if (memberFilter.projectId) memberParams.projectId = memberFilter.projectId;
  else if (memberFilter.streamId) memberParams.streamId = memberFilter.streamId;
  return memberParams;
}

/**
 * Members page server state (TanStack Query).
 */
export function useMembersPageQueries({
  orgId,
  ready,
  debouncedSearch,
  memberFilter,
  canManageInvites,
  filterModalOpen,
  draftFilter,
}: any) {
  const queryClient = useQueryClient();
  const memberParams = useMemo(
    () => buildMemberParams(debouncedSearch, memberFilter),
    [debouncedSearch, memberFilter]
  );

  const membersQuery = useQuery({
    queryKey: orgKeys.members(orgId, memberParams),
    queryFn: () => organisationsAPI.getMembers(orgId, memberParams),
    enabled: !!orgId,
    staleTime: 30_000,
  });

  const invitesQuery = useQuery({
    queryKey: orgKeys.invites(orgId),
    queryFn: () => organisationsAPI.getInvites(orgId),
    enabled: !!orgId,
    staleTime: 30_000,
  });

  const accessRequestsQuery = useQuery({
    queryKey: orgKeys.accessRequests(orgId),
    queryFn: () => accessRequestsAPI.getRequests(orgId),
    enabled: !!orgId && canManageInvites,
    staleTime: 30_000,
  });

  const streamsDropdownQuery = useQuery({
    queryKey: orgKeys.streamsDropdown(orgId),
    queryFn: () => streamsAPI.listDropdown(orgId),
    enabled: !!orgId && filterModalOpen,
    staleTime: 60_000,
  });

  const filterReposQuery = useQuery({
    queryKey: orgKeys.repositories(orgId, '__filter_all__'),
    queryFn: async () => {
      const { repositories } = await organisationsAPI.getRepositories(orgId, { limit: 5000 });
      return Array.isArray(repositories) ? repositories : [];
    },
    enabled: !!orgId && filterModalOpen,
    staleTime: 60_000,
  });

  const filterProjectsQuery = useQuery({
    queryKey: orgKeys.streamProjects(orgId, draftFilter.streamId, { dropdown: true }),
    queryFn: () => projectsAPI.listDropdownByStream(orgId, draftFilter.streamId),
    enabled: !!orgId && filterModalOpen && !!draftFilter.streamId,
    staleTime: 60_000,
  });

  const members = useMemo(() => {
    const list = membersQuery.data;
    return Array.isArray(list) ? list : [];
  }, [membersQuery.data]);

  const invites = useMemo(() => {
    const list = invitesQuery.data;
    return Array.isArray(list) ? list : [];
  }, [invitesQuery.data]);

  const requests = useMemo(() => {
    if (!canManageInvites) return [];
    const list = accessRequestsQuery.data;
    return Array.isArray(list) ? list : [];
  }, [accessRequestsQuery.data, canManageInvites]);

  const streamOptions = useMemo(() => {
    const list = streamsDropdownQuery.data;
    return Array.isArray(list) ? list : [];
  }, [streamsDropdownQuery.data]);

  const orgRepositories = useMemo(() => {
    const list = filterReposQuery.data;
    return Array.isArray(list) ? list : [];
  }, [filterReposQuery.data]);

  const projectOptions = useMemo(() => {
    if (!draftFilter.streamId) return [];
    const list = filterProjectsQuery.data;
    return Array.isArray(list) ? list : [];
  }, [filterProjectsQuery.data, draftFilter.streamId]);

  const loading = !ready || membersQuery.isLoading;

  const invalidateMembers = () => invalidateOrgMembersCaches(queryClient, orgId);

  const deleteInviteMutation = useMutation({
    mutationFn: (inviteId) => organisationsAPI.deleteInvite(orgId, inviteId),
    onSuccess: () => {
      toast.success('Invite cancelled');
      invalidateMembers();
    },
    onError: (err: unknown) => toast.error((err as Error).message || 'Failed to cancel invite'),
  });

  const deleteMemberMutation = useMutation({
    mutationFn: (userId) => organisationsAPI.deleteMember(orgId, userId),
    onSuccess: () => invalidateMembers(),
    onError: (err: unknown) => toast.error((err as Error).message || 'Failed to delete member'),
  });

  const grantRequestMutation = useMutation({
    mutationFn: ({ requestId }: any) => accessRequestsAPI.grantRequest(orgId, requestId),
    onSuccess: () => invalidateMembers(),
    onError: (err: unknown) => toast.error((err as Error).message || 'Failed to send invite'),
  });

  return {
    members,
    invites,
    requests,
    streamOptions,
    orgRepositories,
    projectOptions,
    loading,
    isFetching: membersQuery.isFetching,
    invalidateMembers,
    deleteInviteMutation,
    deleteMemberMutation,
    grantRequestMutation,
    membersQueryError: membersQuery.isError ? membersQuery.error : null,
  };
}
